/**
 * Connections (social) api client — calls same-origin /api/social/* ONLY.
 * ---------------------------------------------------------------------------
 * The friend graph is mutual-consent: request -> accept. Search is gated to
 * the caller's own year group by the Server; cross-year links go through the
 * in-person QR "add me" flow. Bios are moderated via `/api/moderation/check`
 * before being saved (see `BioEditor`).
 */

import type {
  AvatarConfig,
  FriendActionResult,
  FriendRequests,
  FriendUser,
  MyProfile,
  ProfileWithCounts,
  PublicUser,
  QrToken,
} from "@/features/connections/types";

async function request<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(path, {
    credentials: "same-origin",
    ...init,
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
  });
  const payload = (await res.json().catch(() => null)) as
    | T
    | { error?: string }
    | null;
  if (!res.ok) {
    const message =
      (payload && typeof payload === "object" && "error" in payload
        ? payload.error
        : null) ?? "Request failed";
    throw new Error(message);
  }
  return payload as T;
}

// --- Profile ---------------------------------------------------------------
export const getMyProfile = () => request<MyProfile>("/api/social/me");

export const updateBio = (bio: string) =>
  request<MyProfile>("/api/social/me", {
    method: "PATCH",
    body: JSON.stringify({ bio }),
  });

/** Equip a cosmetic avatar frame (see `components/ui/AvatarFrame.tsx`). */
export const updateFrame = (frameId: string) =>
  request<MyProfile>("/api/social/me", {
    method: "PATCH",
    body: JSON.stringify({ avatarBorderKey: frameId }),
  });

/** Equip a hosted avatar PNG and persist the DiceBear recipe for re-edit. */
export const updateAvatar = (avatarKey: string, avatarConfig: AvatarConfig) =>
  request<MyProfile>("/api/social/me", {
    method: "PATCH",
    body: JSON.stringify({ avatarKey, avatarConfig }),
  });

export const getProfile = (userId: string) =>
  request<ProfileWithCounts>(
    `/api/social/profile/${encodeURIComponent(userId)}`,
  );

// --- Discovery (same year group) ------------------------------------------
export const searchPeers = (q: string) =>
  request<PublicUser[]>(`/api/social/search?q=${encodeURIComponent(q)}`);

// --- Friends & requests ----------------------------------------------------
export const listFriends = () => request<FriendUser[]>("/api/social/friends");

export const listRequests = () =>
  request<FriendRequests>("/api/social/friends/requests");

export const sendRequest = (userId: string) =>
  request<FriendActionResult>("/api/social/friends/request", {
    method: "POST",
    body: JSON.stringify({ userId }),
  });

export const acceptRequest = (userId: string) =>
  request<FriendActionResult>(
    `/api/social/friends/${encodeURIComponent(userId)}/accept`,
    { method: "POST" },
  );

export const declineRequest = (userId: string) =>
  request<FriendActionResult>(
    `/api/social/friends/${encodeURIComponent(userId)}/decline`,
    { method: "POST" },
  );

export const removeFriend = (userId: string) =>
  request<FriendActionResult>(
    `/api/social/friends/${encodeURIComponent(userId)}`,
    { method: "DELETE" },
  );

// --- QR "add me" -----------------------------------------------------------
export const getMyQr = () => request<QrToken>("/api/social/qr");

export const rotateMyQr = () =>
  request<QrToken>("/api/social/qr/rotate", { method: "POST" });

export const addByQr = (token: string) =>
  request<FriendActionResult>("/api/social/friends/qr", {
    method: "POST",
    body: JSON.stringify({ token }),
  });
